import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Paper,
  Typography,
  Grid,
  Button,
  LinearProgress,
  Box,
  Chip,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl,
  MenuItem,
  Select,
  TextField
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  statsCard: {
    height: '100%'
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 'bold',
    lineHeight: 1.2
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 220
  },
  sectionTitle: {
    marginBottom: theme.spacing(1)
  },
  tableWrap: {
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  statusChip: {
    minWidth: 86,
    justifyContent: 'center'
  },
  listCompact: {
    paddingTop: 0,
    paddingBottom: 0
  },
  subInfo: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  progressWrap: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  progressBar: {
    height: 10,
    borderRadius: 6,
    marginTop: theme.spacing(0.75)
  },
  requestRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap'
  },
  requestActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  statusSelect: {
    minWidth: 150
  },
  missingSearchBar: {
    marginBottom: theme.spacing(1.5)
  },
  missingHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  singleDialogDetail: {
    marginTop: theme.spacing(1)
  },
  compactTable: {
    '& .MuiTableCell-root': {
      paddingTop: 6,
      paddingBottom: 6
    }
  },
  actionButtonsRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'wrap'
  }
}));

const formatDateTime = (value) => {
  if (!value) return 'Unknown time';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return 'Unknown time';
  }
};

const REQUEST_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const formatRequestStatusLabel = (status) => {
  const found = REQUEST_STATUS_OPTIONS.find((option) => option.value === status);
  return found ? found.label : 'Pending';
};

const FitmentDashboard = () => {
  const classes = useStyles();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [requestStatusDrafts, setRequestStatusDrafts] = useState({});
  const [updatingRequestId, setUpdatingRequestId] = useState(null);
  const [missingSearch, setMissingSearch] = useState('');
  const [singleRequestDialog, setSingleRequestDialog] = useState({
    open: false,
    group: null,
    filterKey: ''
  });
  const [creatingSingleRequest, setCreatingSingleRequest] = useState(false);
  const [debugSearch, setDebugSearch] = useState('');
  const [enrichDialog, setEnrichDialog] = useState({
    open: false,
    group: null,
    filterKey: ''
  });
  const [enrichingGroup, setEnrichingGroup] = useState(false);
  const [lastEnrichResult, setLastEnrichResult] = useState(null);

  const fetchMatrix = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const response = await axios.get('/api/shop/fleet-fitment/filter-matrix', {
        params: {
          vehicleType: 'Vehicle',
          activeOnly: true,
          includeGroups: true,
          includeDebug: true,
          debugLimit: 200,
          includeRecentRequests: true,
          recentRequestsLimit: 10,
          requestHistoryLimit: 250
        }
      });

      setMatrix(response?.data?.data || null);
      setError('');
    } catch (fetchError) {
      console.error('Error loading fleet filter matrix:', fetchError);
      setError(fetchError?.response?.data?.error || 'Failed to load fleet filter status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMatrix();
    const interval = setInterval(() => fetchMatrix({ silent: true }), 30000);
    return () => clearInterval(interval);
  }, []);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const missingByFilter = useMemo(() => {
    const grouped = {};
    const missingGroups = Array.isArray(matrix?.missingGroups) ? matrix.missingGroups : [];
    missingGroups.forEach((group) => {
      const missingFilters = Array.isArray(group.missingFilters) ? group.missingFilters : [];
      missingFilters.forEach((filterKey) => {
        if (!grouped[filterKey]) {
          grouped[filterKey] = { missingGroups: 0, vehicleUnits: 0 };
        }
        grouped[filterKey].missingGroups += 1;
        grouped[filterKey].vehicleUnits += group.vehicleCount || 0;
      });
    });
    return grouped;
  }, [matrix]);

  const filters = Array.isArray(matrix?.filters) ? matrix.filters : [];
  const missingGroups = Array.isArray(matrix?.missingGroups) ? matrix.missingGroups : [];
  const debugSummary = matrix?.debug || {
    withFitmentItems: 0,
    candidateItems: 0,
    nonCandidateWithFitmentItems: 0,
    candidateClassification: {}
  };
  const debugCandidates = Array.isArray(matrix?.debug?.candidates) ? matrix.debug.candidates : [];
  const filteredDebugCandidates = useMemo(() => {
    const term = String(debugSearch || '').trim().toLowerCase();
    if (!term) return debugCandidates;
    return debugCandidates.filter((candidate) => {
      const searchable = [
        candidate.partNumber,
        candidate.title,
        candidate.brand,
        candidate.category,
        candidate.subcategory,
        candidate.matchedFilterKey,
        candidate.classificationSource
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchable.includes(term);
    });
  }, [debugCandidates, debugSearch]);
  const filteredMissingGroups = useMemo(() => {
    const term = String(missingSearch || '').trim().toLowerCase();
    if (!term) return missingGroups;
    return missingGroups.filter((group) => {
      const searchable = `${group.year || ''} ${group.make || ''} ${group.model || ''}`.toLowerCase();
      return searchable.includes(term);
    });
  }, [missingGroups, missingSearch]);
  const requestSummary = matrix?.requestStatusSummary || {
    total: 0,
    byStatus: {},
    openRequests: 0,
    closedRequests: 0,
    completionRate: 0,
    failureRate: 0
  };

  const handleInitiateRequests = async () => {
    setInitiating(true);
    setConfirmDialogOpen(false);

    try {
      const response = await axios.post('/api/shop/fleet-fitment/filter-matrix/initiate-requests', {
        vehicleType: 'Vehicle',
        activeOnly: true
      });

      const payload = response?.data?.data || {};
      if ((payload.requestsCreated || 0) === 0) {
        showSnackbar(payload.message || 'No missing requests to create', 'info');
      } else {
        showSnackbar(`Created ${payload.requestsCreated} request log entries`, 'success');
      }

      await fetchMatrix({ silent: true });
    } catch (requestError) {
      console.error('Error initiating requests:', requestError);
      showSnackbar(requestError?.response?.data?.error || 'Failed to initiate requests', 'error');
    } finally {
      setInitiating(false);
    }
  };

  const handleRequestStatusDraftChange = (requestId, requestStatus) => {
    setRequestStatusDrafts((prev) => ({
      ...prev,
      [requestId]: requestStatus
    }));
  };

  const handleUpdateRequestStatus = async (request) => {
    const selectedStatus = requestStatusDrafts[request.id] || request.requestStatus || 'pending';
    if (selectedStatus === request.requestStatus) {
      return;
    }

    setUpdatingRequestId(request.id);
    try {
      await axios.patch(`/api/shop/fleet-fitment/filter-matrix/requests/${request.id}/status`, {
        requestStatus: selectedStatus
      });
      showSnackbar(`Updated request to ${formatRequestStatusLabel(selectedStatus)}`, 'success');
      setRequestStatusDrafts((prev) => {
        const next = { ...prev };
        delete next[request.id];
        return next;
      });
      await fetchMatrix({ silent: true });
    } catch (updateError) {
      console.error('Error updating request status:', updateError);
      showSnackbar(updateError?.response?.data?.error || 'Failed to update request status', 'error');
    } finally {
      setUpdatingRequestId(null);
    }
  };

  const handleOpenSingleRequestDialog = (group) => {
    const missingFilters = Array.isArray(group?.missingFilters) ? group.missingFilters : [];
    if (!group || missingFilters.length === 0) {
      showSnackbar('No missing filters available for this group', 'warning');
      return;
    }

    setSingleRequestDialog({
      open: true,
      group,
      filterKey: missingFilters[0]
    });
  };

  const handleCreateSingleRequest = async () => {
    if (!singleRequestDialog.group || !singleRequestDialog.filterKey) return;

    setCreatingSingleRequest(true);
    try {
      const response = await axios.post('/api/shop/fleet-fitment/filter-matrix/initiate-request', {
        groupKey: singleRequestDialog.group.key,
        filterKey: singleRequestDialog.filterKey,
        vehicleType: 'Vehicle',
        activeOnly: true
      });

      const request = response?.data?.data?.request;
      const group = response?.data?.data?.targetGroup;
      const label = request?.filterLabel || 'Filter';
      const groupLabel = group ? `${group.year || 'N/A'} ${group.make || ''} ${group.model || ''}`.trim() : 'selected group';
      showSnackbar(`Created 1 test request for ${label} on ${groupLabel}`, 'success');

      setSingleRequestDialog({
        open: false,
        group: null,
        filterKey: ''
      });
      await fetchMatrix({ silent: true });
    } catch (singleError) {
      console.error('Error creating single request:', singleError);
      showSnackbar(singleError?.response?.data?.error || 'Failed to create single request', 'error');
    } finally {
      setCreatingSingleRequest(false);
    }
  };

  const handleOpenEnrichDialog = (group) => {
    const missingFilters = Array.isArray(group?.missingFilters) ? group.missingFilters : [];
    if (!group || missingFilters.length === 0) {
      showSnackbar('No missing filters available for enrichment on this group', 'warning');
      return;
    }

    setEnrichDialog({
      open: true,
      group,
      filterKey: missingFilters[0]
    });
  };

  const handleEnrichMissingGroup = async () => {
    if (!enrichDialog.group || !enrichDialog.filterKey) return;

    setEnrichingGroup(true);
    try {
      const response = await axios.post('/api/shop/fleet-fitment/filter-matrix/enrich-missing-group', {
        groupKey: enrichDialog.group.key,
        filterKey: enrichDialog.filterKey,
        vehicleType: 'Vehicle',
        activeOnly: true,
        saveToInventory: true,
        dryRun: false
      });

      const data = response?.data?.data || {};
      setLastEnrichResult(data);

      if (data.enriched) {
        showSnackbar(
          `Enriched ${data?.filter?.label || 'filter'} with ${data?.selectedMatch?.partNumber || 'a matched part'} (${data?.inventoryAction || 'saved'})`,
          'success'
        );
      } else {
        showSnackbar('No compatible match found for this group/filter yet. Check attempts in logs.', 'warning');
      }

      setEnrichDialog({
        open: false,
        group: null,
        filterKey: ''
      });
      await fetchMatrix({ silent: true });
    } catch (enrichError) {
      console.error('Error enriching missing group:', enrichError);
      showSnackbar(enrichError?.response?.data?.error || 'Failed to enrich missing group', 'error');
    } finally {
      setEnrichingGroup(false);
    }
  };

  if (loading) {
    return (
      <Paper className={classes.root}>
        <Box className={classes.loadingBox}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            Loading fleet fitment status...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!matrix) {
    return (
      <Paper className={classes.root}>
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error || 'Could not load fleet fitment status.'}
        </Alert>
        <Button variant="contained" color="primary" onClick={() => fetchMatrix()}>
          Retry
        </Button>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Fleet Filter Fitment Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Scope: equipment type = Vehicle, active only, uniqueness = Year / Make / Model
          </Typography>
        </Box>

        <Box className={classes.headerActions}>
          <Tooltip title="Refresh status">
            <span>
              <IconButton color="primary" onClick={() => fetchMatrix({ silent: true })} disabled={refreshing || initiating}>
                {refreshing ? <CircularProgress size={22} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AssignmentIcon />}
            onClick={() => setConfirmDialogOpen(true)}
            disabled={initiating || missingGroups.length === 0}
          >
            Initiate Missing Requests
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Vehicles
              </Typography>
              <Typography className={classes.statNumber}>
                {(matrix?.fleet?.totalVehicles || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Unique Vehicle Groups
              </Typography>
              <Typography className={classes.statNumber}>
                {(matrix?.fleet?.uniqueVehicleGroups || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Fully Covered Groups
              </Typography>
              <Typography className={classes.statNumber}>
                {(matrix?.coverage?.fullyCoveredGroups || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                of {(matrix?.coverage?.totalGroups || 0).toLocaleString()} groups
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card className={classes.statsCard}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Missing Coverage Slots
              </Typography>
              <Typography className={classes.statNumber}>
                {(matrix?.coverage?.totalMissingAssignments || 0).toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Oil + Air + Cabin gaps across groups
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box className={classes.progressWrap}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="textSecondary">
            Fully Covered Group Progress
          </Typography>
          <Typography variant="h6" color="primary">
            {matrix?.coverage?.fullyCoveredPercent || 0}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={matrix?.coverage?.fullyCoveredPercent || 0}
          color="primary"
          className={classes.progressBar}
        />
      </Box>

      <Typography variant="h6" className={classes.sectionTitle}>
        Coverage By Filter
      </Typography>
      <Grid container spacing={2} className={classes.subInfo}>
        {filters.map((filter) => {
          const stats = matrix?.coverage?.coverageByFilter?.[filter.key] || {};
          return (
            <Grid item xs={12} md={4} key={filter.key}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">{filter.label}</Typography>
                    <Chip
                      size="small"
                      color={stats.coveragePercent >= 80 ? 'primary' : 'default'}
                      label={`${stats.coveragePercent || 0}%`}
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Covered groups: {(stats.coveredGroups || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Missing groups: {(stats.missingGroups || 0).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="h6" className={classes.sectionTitle}>
        Inventory Fitment Debug
      </Typography>
      <Grid container spacing={2} style={{ marginBottom: 12 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Items With Fitment
              </Typography>
              <Typography className={classes.statNumber}>
                {(debugSummary.withFitmentItems || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Candidates (Core Filters)
              </Typography>
              <Typography className={classes.statNumber}>
                {(debugSummary.candidateItems || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Classified by Text
              </Typography>
              <Typography className={classes.statNumber}>
                {(debugSummary?.candidateClassification?.textMatch || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Fitment Items Not Candidates
              </Typography>
              <Typography className={classes.statNumber}>
                {(debugSummary.nonCandidateWithFitmentItems || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TextField
        variant="outlined"
        size="small"
        label="Search candidate parts"
        value={debugSearch}
        onChange={(event) => setDebugSearch(event.target.value)}
        className={classes.missingSearchBar}
      />
      <TableContainer className={classes.tableWrap} style={{ marginBottom: 16 }}>
        <Table size="small" className={classes.compactTable}>
          <TableHead>
            <TableRow>
              <TableCell>Part #</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Subcategory</TableCell>
              <TableCell>Matched Filter</TableCell>
              <TableCell>Classified By</TableCell>
              <TableCell align="right">Fitment Rows</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDebugCandidates.slice(0, 80).map((candidate) => (
              <TableRow key={candidate.partId}>
                <TableCell>{candidate.partNumber || 'N/A'}</TableCell>
                <TableCell>{candidate.title || 'N/A'}</TableCell>
                <TableCell>{candidate.subcategory || 'N/A'}</TableCell>
                <TableCell>{candidate.matchedFilterKey || 'Unclassified'}</TableCell>
                <TableCell>{candidate.classificationSource || 'none'}</TableCell>
                <TableCell align="right">{(candidate.compatibleVehiclesCount || 0).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {filteredDebugCandidates.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="textSecondary">
                    No debug candidates match the current search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider style={{ marginBottom: 16 }} />

      <Typography variant="h6" className={classes.sectionTitle}>
        Missing Group Details
      </Typography>

      <Box className={classes.missingHeaderRow}>
        <TextField
          variant="outlined"
          size="small"
          label="Search Year / Make / Model"
          value={missingSearch}
          onChange={(event) => setMissingSearch(event.target.value)}
          className={classes.missingSearchBar}
        />
        <Typography variant="body2" color="textSecondary">
          Showing {filteredMissingGroups.length} of {missingGroups.length} missing groups
        </Typography>
      </Box>

      {missingGroups.length === 0 ? (
        <Alert icon={<CheckCircleIcon />} severity="success">
          No filter gaps found. All active vehicle groups have Oil, Air, and Cabin filter coverage.
        </Alert>
      ) : filteredMissingGroups.length === 0 ? (
        <Alert severity="info">
          No missing groups match your search.
        </Alert>
      ) : (
        <TableContainer className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Year</TableCell>
                <TableCell>Make</TableCell>
                <TableCell>Model</TableCell>
                <TableCell align="right">Active Units</TableCell>
                {filters.map((filter) => (
                  <TableCell key={filter.key} align="center">{filter.label}</TableCell>
                ))}
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMissingGroups.map((group) => (
                <TableRow key={group.key} hover>
                  <TableCell>{group.year || 'N/A'}</TableCell>
                  <TableCell>{group.make || 'N/A'}</TableCell>
                  <TableCell>{group.model || 'N/A'}</TableCell>
                  <TableCell align="right">{(group.vehicleCount || 0).toLocaleString()}</TableCell>
                  {filters.map((filter) => {
                    const coverage = group?.coverage?.[filter.key] || {};
                    const label = coverage.covered
                      ? `${coverage.matchedPartCount || 0} matches`
                      : 'Missing';

                    return (
                      <TableCell key={`${group.key}-${filter.key}`} align="center">
                        <Chip
                          size="small"
                          className={classes.statusChip}
                          color={coverage.covered ? 'primary' : 'default'}
                          icon={coverage.covered ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                          label={label}
                        />
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Box className={classes.actionButtonsRow}>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => handleOpenSingleRequestDialog(group)}
                        disabled={!Array.isArray(group.missingFilters) || group.missingFilters.length === 0}
                      >
                        Test 1 Request
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="default"
                        onClick={() => handleOpenEnrichDialog(group)}
                        disabled={!Array.isArray(group.missingFilters) || group.missingFilters.length === 0}
                      >
                        Enrich 1 Part
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box marginTop={3}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Request Success / Failure
        </Typography>

        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Open Requests
                </Typography>
                <Typography className={classes.statNumber}>
                  {(requestSummary.openRequests || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Completed
                </Typography>
                <Typography className={classes.statNumber}>
                  {(requestSummary?.byStatus?.completed || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Failed
                </Typography>
                <Typography className={classes.statNumber}>
                  {(requestSummary?.byStatus?.failed || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Completion Rate
                </Typography>
                <Typography className={classes.statNumber}>
                  {requestSummary.completionRate || 0}%
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Failure Rate: {requestSummary.failureRate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" className={classes.sectionTitle}>
          Recent Request Activity
        </Typography>

        {Array.isArray(matrix?.recentRequests) && matrix.recentRequests.length > 0 ? (
          <List className={classes.listCompact}>
            {matrix.recentRequests.map((request) => (
              <ListItem key={request.id} divider>
                <Box className={classes.requestRow}>
                  <ListItemText
                    primary={request.description}
                    secondary={`${formatDateTime(request.timestamp)} • ${request.createdByName || 'System'} • Last status change: ${formatDateTime(request.requestStatusUpdatedAt || request.timestamp)}`}
                  />
                  <Box className={classes.requestActions}>
                    <Chip
                      size="small"
                      label={formatRequestStatusLabel(request.requestStatus || 'pending')}
                      color={request.requestStatus === 'completed' ? 'primary' : 'default'}
                    />
                    <FormControl variant="outlined" size="small" className={classes.statusSelect}>
                      <Select
                        value={requestStatusDrafts[request.id] || request.requestStatus || 'pending'}
                        onChange={(event) => handleRequestStatusDraftChange(request.id, event.target.value)}
                      >
                        {REQUEST_STATUS_OPTIONS.map((statusOption) => (
                          <MenuItem key={statusOption.value} value={statusOption.value}>
                            {statusOption.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      disabled={
                        updatingRequestId === request.id ||
                        (requestStatusDrafts[request.id] || request.requestStatus || 'pending') === (request.requestStatus || 'pending')
                      }
                      onClick={() => handleUpdateRequestStatus(request)}
                    >
                      {updatingRequestId === request.id ? 'Saving...' : 'Update'}
                    </Button>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No request activity found yet.
          </Typography>
        )}
      </Box>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Initiate Missing Requests</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create request activity entries for missing Oil, Air, and Cabin filter coverage across active vehicle groups.
          </DialogContentText>

          {filters.map((filter) => {
            const missingStats = missingByFilter[filter.key] || { missingGroups: 0, vehicleUnits: 0 };
            return (
              <Typography key={filter.key} variant="body2" style={{ marginTop: 8 }}>
                <strong>{filter.label}:</strong> {missingStats.missingGroups} groups / {missingStats.vehicleUnits} active units
              </Typography>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="default" disabled={initiating}>
            Cancel
          </Button>
          <Button
            onClick={handleInitiateRequests}
            color="primary"
            variant="contained"
            disabled={initiating}
          >
            {initiating ? 'Creating...' : 'Create Requests'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={singleRequestDialog.open}
        onClose={() => setSingleRequestDialog({ open: false, group: null, filterKey: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create One Test Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This creates one request for one missing filter on one vehicle group so you can debug safely.
          </DialogContentText>

          <Typography variant="body2" className={classes.singleDialogDetail}>
            <strong>Vehicle Group:</strong>{' '}
            {singleRequestDialog.group
              ? `${singleRequestDialog.group.year || 'N/A'} ${singleRequestDialog.group.make || ''} ${singleRequestDialog.group.model || ''}`.trim()
              : 'N/A'}
          </Typography>
          <Typography variant="body2" className={classes.singleDialogDetail}>
            <strong>Active Units:</strong> {singleRequestDialog.group?.vehicleCount || 0}
          </Typography>

          <FormControl variant="outlined" size="small" fullWidth style={{ marginTop: 12 }}>
            <Select
              value={singleRequestDialog.filterKey}
              onChange={(event) => setSingleRequestDialog((prev) => ({ ...prev, filterKey: event.target.value }))}
            >
              {(singleRequestDialog.group?.missingFilters || []).map((key) => {
                const filter = filters.find((f) => f.key === key);
                return (
                  <MenuItem key={key} value={key}>
                    {filter?.label || key}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSingleRequestDialog({ open: false, group: null, filterKey: '' })}
            color="default"
            disabled={creatingSingleRequest}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateSingleRequest}
            color="primary"
            variant="contained"
            disabled={creatingSingleRequest || !singleRequestDialog.filterKey}
          >
            {creatingSingleRequest ? 'Creating...' : 'Create 1 Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={enrichDialog.open}
        onClose={() => setEnrichDialog({ open: false, group: null, filterKey: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enrich One Missing Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will attempt live O&apos;Reilly lookup for one missing group/filter and, if compatible, save a quantity-0 inventory item with fitment data.
          </DialogContentText>

          <Typography variant="body2" className={classes.singleDialogDetail}>
            <strong>Vehicle Group:</strong>{' '}
            {enrichDialog.group
              ? `${enrichDialog.group.year || 'N/A'} ${enrichDialog.group.make || ''} ${enrichDialog.group.model || ''}`.trim()
              : 'N/A'}
          </Typography>
          <Typography variant="body2" className={classes.singleDialogDetail}>
            <strong>Active Units:</strong> {enrichDialog.group?.vehicleCount || 0}
          </Typography>

          <FormControl variant="outlined" size="small" fullWidth style={{ marginTop: 12 }}>
            <Select
              value={enrichDialog.filterKey}
              onChange={(event) => setEnrichDialog((prev) => ({ ...prev, filterKey: event.target.value }))}
            >
              {(enrichDialog.group?.missingFilters || []).map((key) => {
                const filter = filters.find((f) => f.key === key);
                return (
                  <MenuItem key={key} value={key}>
                    {filter?.label || key}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEnrichDialog({ open: false, group: null, filterKey: '' })}
            color="default"
            disabled={enrichingGroup}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEnrichMissingGroup}
            color="primary"
            variant="contained"
            disabled={enrichingGroup || !enrichDialog.filterKey}
          >
            {enrichingGroup ? 'Enriching...' : 'Run Enrichment'}
          </Button>
        </DialogActions>
      </Dialog>

      {lastEnrichResult && (
        <Alert severity={lastEnrichResult.enriched ? 'success' : 'warning'} style={{ marginTop: 16 }}>
          <Typography variant="body2">
            {lastEnrichResult.enriched
              ? `Last enrichment: ${lastEnrichResult?.selectedMatch?.partNumber || 'Unknown part'} (${lastEnrichResult?.inventoryAction || 'saved'})`
              : 'Last enrichment did not find a compatible part yet.'}
          </Typography>
          <Typography variant="caption" display="block">
            VIN coverage in group: {lastEnrichResult?.fleetContext?.vinVehicleCount || 0}/{lastEnrichResult?.fleetContext?.groupVehicleCount || 0}
            {` (${lastEnrichResult?.fleetContext?.vinCoveragePercent || 0}%)`}
            {lastEnrichResult?.fleetContext?.vinCodes?.length
              ? ` | VIN codes used: ${lastEnrichResult.fleetContext.vinCodes.join(', ')}`
              : ' | No VIN codes available, used Year/Make/Model matching'}
          </Typography>
          {!lastEnrichResult.enriched && Array.isArray(lastEnrichResult?.attempts) && lastEnrichResult.attempts.length > 0 && (
            <Typography variant="caption" display="block">
              Last attempt: {lastEnrichResult.attempts[lastEnrichResult.attempts.length - 1]?.query || 'N/A'}
              {' | '}match by Y/M/M: {lastEnrichResult.attempts[lastEnrichResult.attempts.length - 1]?.matchedByYmm ? 'yes' : 'no'}
              {' | '}match by VIN code: {lastEnrichResult.attempts[lastEnrichResult.attempts.length - 1]?.matchedByVinCode ? 'yes' : 'no'}
            </Typography>
          )}
        </Alert>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default FitmentDashboard;
